import { Spinner } from '@frontend/components/common';
import { SensorLockerStatus } from '@frontend/device/types';
import { Slot } from '@frontend/slot/types';
import { useEffect, useMemo, useState } from 'react';
import { LuDoorClosed, LuDoorOpen, LuTriangleAlert } from 'react-icons/lu';

interface SmallSlotCardProps {
    slot: Slot;
    status: SensorLockerStatus | undefined;
}

const SmallSlotCard = ({ slot, status }: SmallSlotCardProps) => {
    const [timerExpired, setTimerExpired] = useState(false);

    useEffect(() => {
        if (status === SensorLockerStatus.OPENED) {
            const timeoutId = setTimeout(() => {
                setTimerExpired(true);
            }, 30000);

            return () => clearTimeout(timeoutId);
        } else {
            setTimerExpired(false);
        }
    }, [status]);

    const { Icon, color } = useMemo(() => {
        const color = getColorByStatus(status, timerExpired);
        const Icon = getIconByStatus(status);
        return { Icon, color };
    }, [status, timerExpired]);

    return (
        <div className='bg-background-surface border-border-default inline-flex items-center justify-between self-stretch rounded-xl border p-2'>
            <div className='flex h-6 items-center justify-start gap-2'>
                <div className={`${color} flex size-6 items-center justify-center rounded-full`}>
                    <Icon className='text-background-surface size-3' />
                </div>
                <div className='text-foreground-default justify-start text-base font-semibold'>{slot.display}</div>
            </div>

            {timerExpired && <LuTriangleAlert className='text-status-warning size-5' />}
            {status === SensorLockerStatus.OPENING && <Spinner className='text-border-default fill-brand-secondary size-5' />}
        </div>
    );
};

export default SmallSlotCard;

const getColorByStatus = (status: SensorLockerStatus | undefined, timerExpired?: boolean): string => {
    switch (status) {
        case SensorLockerStatus.CLOSED:
            return 'bg-brand-muted';
        case SensorLockerStatus.OPENED:
            if (timerExpired === true) return 'bg-status-warning';
            return 'bg-status-info';
        case SensorLockerStatus.OPENING:
            return 'bg-status-info';
        case SensorLockerStatus.NOT_CLOSED:
            return 'bg-status-warning';
        case SensorLockerStatus.ERROR:
        case SensorLockerStatus.NOT_OPENED:
            return 'bg-status-error';
        default:
            return 'bg-status-info';
    }
};

const getIconByStatus = (status: SensorLockerStatus | undefined) => {
    switch (status) {
        case SensorLockerStatus.NOT_OPENED:
        case SensorLockerStatus.CLOSED:
            return LuDoorClosed;
        case SensorLockerStatus.OPENING:
        case SensorLockerStatus.NOT_CLOSED:
        case SensorLockerStatus.OPENED:
            return LuDoorOpen;
        case SensorLockerStatus.ERROR:
            return LuTriangleAlert;
        default:
            return () => null;
    }
};
